<template>
  <v-card class="fill-height d-flex flex-column">
    <v-card-title>
      Assigned Defects
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close-modal')">
        <v-icon>close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <div v-for="(group, assignedTo) in groupedAssignments" :key="assignedTo" class="subtitle-1 mt-4">
        Assigned To: {{ assignedTo }}
        <v-data-table disable-pagination :items="group" :headers="assignmentHeaders" :loading="$wait.is(['loading assignments'])" hide-default-footer>
          <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
          <template v-slot:no-data>
            <v-layout row align-center>
              <v-flex>There are no assignments</v-flex>
            </v-layout>
          </template>
          <template v-slot:headers="{ props }">
            <tr>
              <th v-for="header in props.headers" :key="header.text" :style="{ width: header.width }">
                {{ header.text }}
              </th>
            </tr>
          </template>
          <template v-slot:item="props">
            <tr>
              <td>{{ props.item.ednNumber }}/{{ props.item.lineNumber }}</td>
              <td>{{ props.item.unitNumber }}</td>
              <td>{{ props.item.description }}</td>
              <td>{{ moment(props.item.receivedDate) | formatDateShort }}</td>
              <td>{{ props.item.status }}</td>
              <td :class="{ 'active-timer': props.item.isActive }">
                <v-chip outlined small v-if="props.item.totalSecondsWorked">
                  {{ formatDuration(props.item.totalSecondsWorked || 0) }}
                </v-chip>
                <v-chip outlined small color="success" v-if="props.item.isActive && props.item.startedTimestamp">
                  {{ formatStopwatch(elapsedSeconds(props.item.startedTimestamp)) }}
                </v-chip>
              </td>
              <td>
                <v-icon v-if="props.item.hasHivis && props.item.started" color="green">check</v-icon>
                <v-icon v-else-if="!props.item.hasHivis && props.item.started" color="red">close</v-icon>
                <span v-else color="grey">Job not started</span>
              </td>
              <td>{{ props.item.category }}</td>
              <td>{{ props.item.subCategory }}</td>
              <td>{{ props.item.notes }}</td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import * as _ from 'lodash';

export default {
  props: {
    assignments: {
      required: true
    }
  },
  data() {
    return {
      assignmentHeaders: [
        { text: 'EDN/Number', value: 'ednNumber', width: '150px' },
        { text: 'Unit', value: 'unitNumber', width: '80px' },
        { text: 'Description', value: 'description', width: '300px' },
        { text: 'Received Date', value: 'receivedDate', width: '100px' },
        { text: 'Status', value: 'status', width: '80px' },
        { text: 'Timer', value: 'totalSecondsWorked', width: '170px' },
        { text: 'HIVIS', value: 'hasHivis', width: '130px' },
        { text: 'Category', value: 'category', width: '140px' },
        { text: 'Subcategory', value: 'subCategory', width: '100px' },
        { text: 'Notes', value: 'notes', width: '100px' }
      ],
      timerHandle: null,
      currentTime: Date.now()
    };
  },
  created() {
    // Tick every second so computed timers update
    this.timerHandle = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);
  },

  beforeDestroy() {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
    }
  },
  computed: {
    groupedAssignments() {
      return _.groupBy(this.assignments, item => item.assignedTo || 'Unassigned');
    }
  },
  methods: {
    // formatDuration, formatStopwatch, elapsedSeconds lifted from MobileWeb project in utils.ts
    formatDuration(totalSeconds) {
      const secondsInMinute = 60;
      const secondsInHour = 60 * secondsInMinute;

      if (totalSeconds == 0) {
        return '';
      }

      if (totalSeconds < secondsInMinute) {
        return '< 1 min';
      }

      if (totalSeconds < secondsInHour) {
        const theseMinutes = Math.floor(totalSeconds / secondsInMinute);
        return `${theseMinutes} minute${theseMinutes !== 1 ? 's' : ''}`;
      }

      const hours = Math.floor(totalSeconds / secondsInHour);
      const minutes = Math.floor((totalSeconds % secondsInHour) / secondsInMinute);
      return `${hours} hour${hours !== 1 ? 's' : ''}` + (minutes ? `, ${minutes} minute${minutes !== 1 ? 's' : ''}` : '');
    },
    formatStopwatch(totalSeconds) {
      if (totalSeconds === 0) {
        return '';
      }

      const secondsInMinute = 60;
      const secondsInHour = 60 * secondsInMinute;

      const hours = Math.floor(totalSeconds / secondsInHour);
      const minutes = Math.floor((totalSeconds % secondsInHour) / secondsInMinute);
      const seconds = totalSeconds % secondsInMinute;

      const padZero = num => num.toString().padStart(2, '0');

      return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
    },
    elapsedSeconds(startTimestamp) {
      if (!startTimestamp) {
        return 0;
      }

      // HACK: Force into UTC time. Datetime handling between .NET and JS sucks and I'm sick of messing around with it.
      const startTimeUTC = Date.parse(startTimestamp + 'Z');
      const nowUTC = this.currentTime;

      return Math.floor((nowUTC - startTimeUTC) / 1000);
    }
  }
};
</script>

<style scoped>
.active-timer {
  color: #4caf50; /* Green color for active timers */
}
</style>
